import React, { useEffect, useMemo, useState } from 'react';

// RelativeTime: shows human-friendly time relative to now and updates at the
// smallest necessary interval. After ~30 days, shows an absolute long date.
// Props:
// - value: Date | string | number (Date, ISO string, or epoch ms)
// - className?: string
// - prefix?: string (e.g., "Created: ")
// - showSuffix?: boolean (default true, e.g., "ago")

const MONTH_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function toDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'number') return new Date(val);
  return new Date(String(val));
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fullDateString(d) {
  try {
    const month = d.toLocaleString(undefined, { month: 'long' });
    const day = ordinal(d.getDate());
    const year = d.getFullYear();
    return `${month} ${day}, ${year}`;
  } catch {
    return d.toString();
  }
}

function exactDateTime(d) {
  try {
    return d.toLocaleString();
  } catch {
    return d.toString();
  }
}

function computeLabelAndNext(msDiff, dt, showSuffix) {
  const abs = Math.abs(msDiff);
  const suffix = showSuffix ? ' ago' : '';
  if (abs < 60 * 1000) {
    // within a minute
    const nextIn = 1000 - (abs % 1000) || 1000;
    return { label: 'just now', nextIn };
  }
  const mins = Math.floor(abs / 60000);
  if (abs < 60 * 60 * 1000) {
    const label = `${mins} minute${mins === 1 ? '' : 's'}${suffix}`;
    const nextIn = 60000 - (abs % 60000) || 60000;
    return { label, nextIn };
  }
  const hours = Math.floor(abs / 3600000);
  if (abs < 24 * 3600000) {
    const label = `${hours} hour${hours === 1 ? '' : 's'}${suffix}`;
    const nextIn = 3600000 - (abs % 3600000) || 3600000;
    return { label, nextIn };
  }
  const days = Math.floor(abs / 86400000);
  if (abs < 7 * 86400000) {
    const label = `${days} day${days === 1 ? '' : 's'}${suffix}`;
    const nextIn = 86400000 - (abs % 86400000) || 86400000;
    return { label, nextIn };
  }
  const weeks = Math.floor(days / 7);
  if (abs < MONTH_THRESHOLD_MS) {
    const label = `${weeks} week${weeks === 1 ? '' : 's'}${suffix}`;
    const nextIn = 86400000; // update daily while in "weeks" range
    return { label, nextIn };
  }
  // 30+ days -> absolute full date
  return { label: fullDateString(dt), nextIn: null };
}

export default function RelativeTime({ value, className = '', prefix = '', showSuffix = true }) {
  const date = useMemo(() => toDate(value), [value]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!date) return;
    const tick = () => setNow(Date.now());
    // Determine initial next tick
    const setupTimer = () => {
      const diff = now - date.getTime();
      const { nextIn } = computeLabelAndNext(diff, date, showSuffix);
      if (!nextIn) return null;
      return setTimeout(() => {
        tick();
      }, nextIn);
    };
    let tm = setupTimer();
    return () => {
      if (tm) clearTimeout(tm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, now, showSuffix]);

  if (!date) return <span className={className}>—</span>;
  const diff = now - date.getTime();
  const { label } = computeLabelAndNext(diff, date, showSuffix);
  const title = exactDateTime(date);
  return (
    <span className={className} title={title} aria-label={title}>
      {prefix}{label}
    </span>
  );
}
